import React from "react";
import SvgMathLabel from "./SvgMathLabel";

function pt(x, y) {
  return `${x},${y}`;
}

export default function CuboidBoxDiagram({
  widthValue = 5,
  depthValue = 6,
  heightValue = 7,
  frontLabel = null,
  depthLabel = null,
  heightLabel = null,
  showInnerCube = false,
  innerCubeEdgeValue = null,
  innerCubeRatio = 0.34,
  width = 320,
  height = 220,
  accent = "#ff9f0a",
  cubeColor = "#ff9f0a",
  fill = "rgba(255,159,10,0.16)",
  style,
  showVertexLabels = false,
}) {
  const Wv = Math.max(1, Number(widthValue) || 1);
  const Dv = Math.max(1, Number(depthValue) || 1);
  const Hv = Math.max(1, Number(heightValue) || 1);

  const marginL = 50;
  const marginR = 22;
  const topMargin = 26;
  const baseY = height - 34;
  const depthXFactor = 0.56;
  const depthYFactor = 0.42;
  const maxScaleByWidth = (width - marginL - marginR) / (Wv + Dv * depthXFactor);
  const maxScaleByHeight = (baseY - topMargin) / (Hv + Dv * depthYFactor);
  const unit = Math.max(8, Math.min(54, maxScaleByWidth, maxScaleByHeight));
  const wPx = Wv * unit;
  const hPx = Hv * unit;
  const dX = Dv * unit * depthXFactor;
  const dY = -Dv * unit * depthYFactor;

  const A = { x: marginL, y: baseY };
  const B = { x: A.x + wPx, y: A.y };
  const D = { x: A.x + dX, y: A.y + dY };
  const C = { x: B.x + dX, y: B.y + dY };
  const A1 = { x: A.x, y: A.y - hPx };
  const B1 = { x: B.x, y: B.y - hPx };
  const D1 = { x: D.x, y: D.y - hPx };
  const C1 = { x: C.x, y: C.y - hPx };

  const hidden = "rgba(180,180,190,0.62)";
  const line = "currentColor";
  const cubeEdgeValue = Number(innerCubeEdgeValue);
  const cubeWidthRatio = cubeEdgeValue > 0 ? Math.min(1, cubeEdgeValue / Wv) : innerCubeRatio;
  const cubeDepthRatio = cubeEdgeValue > 0 ? Math.min(1, cubeEdgeValue / Dv) : innerCubeRatio;
  const cubeHeightRatio = cubeEdgeValue > 0 ? Math.min(1, cubeEdgeValue / Hv) : innerCubeRatio;
  const cubeWidth = wPx * cubeWidthRatio;
  const cubeHeight = hPx * cubeHeightRatio;
  const cdx = dX * cubeDepthRatio;
  const cdy = dY * cubeDepthRatio;
  const cx = B.x - cubeWidth;
  const cy = B.y;
  const cube = {
    A: { x: cx, y: cy },
    B: { x: B.x, y: cy },
    D: { x: cx + cdx, y: cy + cdy },
    C: { x: B.x + cdx, y: cy + cdy },
  };
  cube.A1 = { x: cube.A.x, y: cube.A.y - cubeHeight };
  cube.B1 = { x: cube.B.x, y: cube.B.y - cubeHeight };
  cube.D1 = { x: cube.D.x, y: cube.D.y - cubeHeight };
  cube.C1 = { x: cube.C.x, y: cube.C.y - cubeHeight };

  // Etykieta krawędzi głębokości: wyśrodkowana na krawędzi A→D i odsunięta
  // prostopadle po ZEWNĘTRZNEJ stronie przekątnej (górno-lewej, przy rogu),
  // a nie do środka bryły na ścianę frontową.
  const depthDX = D.x - A.x;
  const depthDY = D.y - A.y;
  const depthLen = Math.hypot(depthDX, depthDY) || 1;
  const depthOff = 16;
  const depthLabelX = (A.x + D.x) / 2 + (depthDY / depthLen) * depthOff;
  const depthLabelY = (A.y + D.y) / 2 + (-depthDX / depthLen) * depthOff;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="Prostopadłościan z oznaczonymi krawędziami"
      style={{ display: "block", maxWidth: "100%", height: "auto", margin: "0 auto", overflow: "visible", ...style }}
    >
      <polygon points={`${pt(A1.x, A1.y)} ${pt(B1.x, B1.y)} ${pt(C1.x, C1.y)} ${pt(D1.x, D1.y)}`} fill={fill} stroke={line} strokeWidth="2.5" />
      <polygon points={`${pt(A.x, A.y)} ${pt(B.x, B.y)} ${pt(B1.x, B1.y)} ${pt(A1.x, A1.y)}`} fill={fill} stroke={line} strokeWidth="2.5" />
      <polygon points={`${pt(B.x, B.y)} ${pt(C.x, C.y)} ${pt(C1.x, C1.y)} ${pt(B1.x, B1.y)}`} fill={fill} stroke={line} strokeWidth="2.5" />

      <line x1={A.x} y1={A.y} x2={D.x} y2={D.y} stroke={hidden} strokeWidth="1.6" strokeDasharray="5 4" />
      <line x1={D.x} y1={D.y} x2={C.x} y2={C.y} stroke={hidden} strokeWidth="1.6" strokeDasharray="5 4" />
      <line x1={D.x} y1={D.y} x2={D1.x} y2={D1.y} stroke={hidden} strokeWidth="1.6" strokeDasharray="5 4" />

      {showInnerCube && (
        <g>
          <polygon points={`${pt(cube.A1.x, cube.A1.y)} ${pt(cube.B1.x, cube.B1.y)} ${pt(cube.C1.x, cube.C1.y)} ${pt(cube.D1.x, cube.D1.y)}`} fill="rgba(255,159,10,0.18)" stroke={cubeColor} strokeWidth="2" />
          <polygon points={`${pt(cube.A.x, cube.A.y)} ${pt(cube.B.x, cube.B.y)} ${pt(cube.B1.x, cube.B1.y)} ${pt(cube.A1.x, cube.A1.y)}`} fill="rgba(255,159,10,0.16)" stroke={cubeColor} strokeWidth="2" />
          <polygon points={`${pt(cube.B.x, cube.B.y)} ${pt(cube.C.x, cube.C.y)} ${pt(cube.C1.x, cube.C1.y)} ${pt(cube.B1.x, cube.B1.y)}`} fill="rgba(255,159,10,0.14)" stroke={cubeColor} strokeWidth="2" />
          <line x1={cube.A.x} y1={cube.A.y} x2={cube.D.x} y2={cube.D.y} stroke={cubeColor} strokeWidth="1.5" strokeDasharray="4 3" />
          <line x1={cube.D.x} y1={cube.D.y} x2={cube.C.x} y2={cube.C.y} stroke={cubeColor} strokeWidth="1.5" strokeDasharray="4 3" />
          <line x1={cube.D.x} y1={cube.D.y} x2={cube.D1.x} y2={cube.D1.y} stroke={cubeColor} strokeWidth="1.5" strokeDasharray="4 3" />
        </g>
      )}

      <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke={accent} strokeWidth="4" />
      <line x1={A.x} y1={A.y} x2={D.x} y2={D.y} stroke={accent} strokeWidth="4" />
      <line x1={A.x} y1={A.y} x2={A1.x} y2={A1.y} stroke={accent} strokeWidth="4" />

      {frontLabel && <SvgMathLabel x={(A.x + B.x) / 2} y={A.y + 20} math={frontLabel} fontSize={16} color={line} />}
      {depthLabel && <SvgMathLabel x={depthLabelX} y={depthLabelY} math={depthLabel} fontSize={16} color={line} />}
      {heightLabel && <SvgMathLabel x={A.x - 20} y={(A.y + A1.y) / 2} math={heightLabel} fontSize={16} color={line} />}
      {showVertexLabels && [[A,"A",-12,12],[B,"B",12,12],[C,"C",12,12],[D,"D",-12,-10],[A1,"E",-12,-12],[B1,"F",0,-16],[C1,"G",12,-6],[D1,"H",-12,-12]].map(([p,name,dx,dy])=><SvgMathLabel key={name} x={p.x+dx} y={p.y+dy} math={name} fontSize={15} color={line}/>)}
    </svg>
  );
}
